import { Box, TextField, Typography } from '@mui/material';
import Stack from '@mui/system/Stack';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { SplitScreenLayout, getPathnamesOfItems } from '@starjack/layout';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useResolvedSidebarEntries } from '@/layouts/sidebar/SidebarLayout';
import Alert from '@mui/material/Alert';
import StarjackLogo from '@/assets/logo/starjack-head-pos.png';
import { PageWithLayout } from '@/models/page';

const loginErrorMessages = (error: string): string => {
    switch (error) {
        case 'bad_credentials':
            return 'Der Benutzername oder das Passwort ist falsch.';
        default:
            return 'Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuchen Sie es später noch einmal';
    }
};

const Login: PageWithLayout = () => {
    const { login, isLoggedIn } = useAuthentication();
    const [isLoading, setIsLoading] = useState(false);
    const [loginError, setLoginError] = useState<string>(null);
    const resolvedEntries = useResolvedSidebarEntries();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const query = Object.fromEntries(searchParams.entries());
    const formik = useFormik<{ username: string; password: string }>({
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string()
                .max(255)
                .required('Bitte geben Sie Ihren Benutzername ein'),
            password: Yup.string()
                .max(255)
                .required('Bitte geben Sie Ihr Passwort ein'),
        }),
        onSubmit: async (values) => {
            setLoginError(null);
            setIsLoading(true);
            const [, error] = await login(values.username, values.password);

            if (error) {
                setLoginError(error);
            }

            setIsLoading(false);
        },
    });
    const [loggedIn] = isLoggedIn();

    const getHomePagePath = (): string | null => {
        const [loggedIn] = isLoggedIn();
        if (loggedIn) {
            const urlPath = query.redirectUrl;
            if (urlPath) return urlPath as string;
            const pathnames = getPathnamesOfItems(resolvedEntries);
            return pathnames[0] ?? '/';
        }
        return null;
    };

    useEffect(() => {
        document.title = 'Anmeldung | Starjack B2B';
    }, []);

    useEffect(() => {
        if (loggedIn) {
            const homePagePath = getHomePagePath();

            if (homePagePath) {
                navigate(homePagePath);
                return;
            }
        }
    }, [loggedIn, resolvedEntries, navigate]);

    return (
        <>
            <Box
                sx={{
                    backgroundColor: 'background.paper',
                    flex: '1 1 auto',
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        maxWidth: 550,
                        px: 3,
                        py: '100px',
                        width: '100%',
                    }}
                >
                    <div>
                        <Typography
                            sx={{
                                mb: 4,
                            }}
                            variant='h4'
                        >
                            Anmeldung
                        </Typography>
                        <form noValidate onSubmit={formik.handleSubmit}>
                            <Stack
                                sx={{
                                    mb: 4,
                                }}
                                spacing={3}
                            >
                                {!!loginError && (
                                    <Alert
                                        severity='error'
                                        color='error'
                                        variant='filled'
                                    >
                                        {loginErrorMessages(loginError)}
                                    </Alert>
                                )}

                                <TextField
                                    error={
                                        !!(
                                            formik.touched.username &&
                                            formik.errors.username
                                        )
                                    }
                                    fullWidth
                                    helperText={
                                        formik.touched.username &&
                                        formik.errors.username
                                    }
                                    label='Benutzername'
                                    name='username'
                                    onBlur={formik.handleBlur}
                                    type='text'
                                    onChange={formik.handleChange}
                                    value={formik.values.username}
                                />
                                <TextField
                                    error={
                                        !!(
                                            formik.touched.password &&
                                            formik.errors.password
                                        )
                                    }
                                    fullWidth
                                    helperText={
                                        formik.touched.password &&
                                        formik.errors.password
                                    }
                                    label='Password'
                                    name='password'
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    type='password'
                                    value={formik.values.password}
                                />
                            </Stack>

                            <LoadingButton
                                fullWidth
                                size='large'
                                sx={{ mt: 3 }}
                                type='submit'
                                variant='contained'
                                loading={isLoading}
                            >
                                Anmelden
                            </LoadingButton>
                        </form>
                    </div>
                </Box>
            </Box>
        </>
    );
};

Login.isProtected = false;
Login.getLayout = (page) => (
    <SplitScreenLayout
        logoTo='/'
        logoSrc={StarjackLogo}
        logoAlt='Starjack Logo'
        appName='starjack B2B'
        subtitle='Das Portal für Ihre Ticket Buchungen'
    >
        {page}
    </SplitScreenLayout>
);

export default Login;
